import {GetRecommendationsPageResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/product_recommendations_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  FluidGrid,
  FONT_SIZES,
  FONT_WEIGHTS,
  Text,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React from 'react';
import {ProductTile} from '../../../features/ProductTile/ProductTile';
import {ScrollSimilarItemsView} from '../SimilarItemsScrollIntoView/ScrollSimilarItemsView';
import {useApiABTestSimilarItems} from '../utils/useApiABTestSimilarItems';

export const TILES_TO_SHOW = 10;

export interface SimilarProductsQuickViewViewProps {
  className?: string;
  discoverMore: React.ReactNode;
  setShowSimilarItemsButton: (setShouldShowSimilarItems: boolean) => void;
  showScrollToSimilarItemsButton?: boolean;
}

export const SimilarProductsQuickViewView = ({
  className,
  discoverMore,
  setShowSimilarItemsButton,
  recommendations,
  ref,
}: GetRecommendationsPageResponse &
  SimilarProductsQuickViewViewProps & {ref?: React.Ref<HTMLElement>}) => {
  const {t} = useTranslation();

  useApiABTestSimilarItems();

  const {ref: firstProductTileRef} = useInViewEffect<HTMLLIElement>(
    inView => {
      if (inView) {
        setShowSimilarItemsButton(false);
      }
    },
    {
      triggerOnce: true,
      threshold: 1,
    },
  );

  if (!recommendations.length) {
    return null;
  }

  return (
    <Section className={className} ref={ref}>
      <ScrollSimilarItemsView />
      <Title>{t('Similar Items')}</Title>

      <FluidGrid>
        {recommendations.slice(0, TILES_TO_SHOW).map((recommendation, index) =>
          index === 0 ? (
            <li ref={firstProductTileRef} key={recommendation.productId}>
              <ProductTile
                tile={recommendation}
                streamPosition={index}
                gridColumns={2}
              />
            </li>
          ) : (
            <ProductTile
              key={recommendation.productId}
              tile={recommendation}
              streamPosition={index}
              gridColumns={2}
            />
          ),
        )}
      </FluidGrid>
      <Gradient data-testid="similarProductsQuickView-gradient">
        {discoverMore}
      </Gradient>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  margin-bottom: -220px;
`;

const Title = styled(Text)`
  margin-bottom: 10px;
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: ${FONT_SIZES.px20};
`;

const Gradient = styled.div`
  position: absolute;
  bottom: 120px;
  left: 0;
  right: 0;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    #ffffff 59.83%
  );
`;
